import React, { forwardRef, memo, useCallback, useMemo } from 'react';
import { OptionsList, type OptionsListProps } from '@alfalab/core-components-select/shared';
import type { SelectProps } from '@alfalab/core-components-select';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';

import type { TSelectV2Desktop } from '../models/desktop';
import { sizeMapper } from '../config';

export const SelectV2DesktopRenderer = memo<TSelectV2Desktop>(
    forwardRef(
        (
            {
                Select,
                children,
                limitDynamicOptionGroupSize,
                onChange,
                showConfirmationClear,
                showConfirmationSelectAll,
                showEmptyOptionsList = true,
                size = 's',
                useControlled,
                useSelectWithApply,
                withConfirmation,
                ...props
            },
            ref
        ) => {
            const { header } = useNamedContainers(children);

            const isConfirmationRequired = props.multiple && withConfirmation;

            const [selected, setSelected] = useControlled({
                controlled: props.selected,
                default: [],
            });

            const handleChange = useCallback<NonNullable<SelectProps['onChange']>>(
                payload => {
                    setSelected(payload.selectedMultiple.map(option => option.key));
                    onChange?.(payload);
                },
                [onChange, setSelected]
            );

            const getOptionsList = useCallback(
                (args: OptionsListProps) => <OptionsList {...args} header={header} />,
                [header]
            );

            const useSelectWithApplyArgs = useMemo(
                () => ({
                    options: props.options,
                    selected,
                    onChange: handleChange,
                    showClear: showConfirmationClear,
                    showSelectAll: showConfirmationSelectAll,
                    OptionsList: getOptionsList,
                }),
                [
                    props.options,
                    selected,
                    handleChange,
                    showConfirmationClear,
                    showConfirmationSelectAll,
                    getOptionsList,
                ]
            );

            const selectPropsWithConfirmation = useSelectWithApply(useSelectWithApplyArgs);

            const selectionProps = useMemo(
                () =>
                    isConfirmationRequired
                        ? selectPropsWithConfirmation
                        : {
                              selected,
                              onChange: handleChange,
                          },
                [isConfirmationRequired, selectPropsWithConfirmation, selected, handleChange]
            );

            return (
                <Select
                    {...props}
                    ref={ref}
                    limitDynamicOptionGroupSize={limitDynamicOptionGroupSize}
                    size={sizeMapper[size]}
                    {...selectionProps}
                    showEmptyOptionsList={showEmptyOptionsList}
                />
            );
        }
    )
);
